// ─── CSV Export Utilities ──────────────────────────────────
import { GroupRow, TeamRow, UserRow } from './types';

/**
 * Escape a value for CSV: wrap in quotes if it contains commas,
 * quotes, or newlines.  Double any internal quotes.
 */
function csvCell(value: string | number): string {
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

/** Join a row of values into a CSV line. */
function csvRow(cells: (string | number)[]): string {
    return cells.map(csvCell).join(',');
}

/** Trigger a browser download of a CSV string. */
function downloadCsv(csv: string, filename: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/** Format today's date as YYYY-MM-DD for filenames. */
function todayStamp(): string {
    return new Date().toISOString().slice(0, 10);
}

// ─── Level 1: Groups ─────────────────────────────────────
export function exportGroupsCsv(groups: GroupRow[]): void {
    const header = ['Group Name', 'Teams', 'Users', 'Active', 'Inactive', '% Active', 'Trend', 'Trend %'];
    const rows = groups.map((g) =>
        csvRow([
            g.name,
            g.teamCount,
            g.userCount,
            g.activeCount,
            g.inactiveCount,
            g.activePercent,
            g.trend,
            g.trendValue,
        ])
    );
    const csv = [csvRow(header), ...rows].join('\n');
    downloadCsv(csv, `groups-activity-${todayStamp()}.csv`);
}

// ─── Level 2: Teams ──────────────────────────────────────
export function exportTeamsCsv(teams: TeamRow[], groupName: string): void {
    const header = ['Team Name', 'Tags', 'Users', 'Active', 'Inactive', '% Active', 'Trend', 'Trend %'];
    const rows = teams.map((t) =>
        csvRow([
            t.name,
            t.tags.join('; '),
            t.userCount,
            t.activeCount,
            t.inactiveCount,
            t.activePercent,
            t.trend,
            t.trendValue,
        ])
    );
    const csv = [csvRow(header), ...rows].join('\n');
    const safeName = groupName.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-').toLowerCase();
    downloadCsv(csv, `${safeName}-teams-activity-${todayStamp()}.csv`);
}

// ─── Level 3: Users ──────────────────────────────────────
export function exportUsersCsv(users: UserRow[], teamName: string): void {
    const header = [
        'User Name', 'Tags', 'Days Active', 'SOS', 'Fake Call',
        'Check On Me', 'Safety Reports', 'Risk Reviews', 'Last Active', 'Status',
    ];
    const rows = users.map((u) =>
        csvRow([
            u.name,
            u.tags.join('; '),
            u.daysActive,
            u.sosCount,
            u.fakeCallCount,
            u.checkOnMeCount,
            u.safetyReportCount,
            u.riskReviewCount,
            u.lastActive,
            u.status,
        ])
    );
    const csv = [csvRow(header), ...rows].join('\n');
    const safeName = teamName.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-').toLowerCase();
    downloadCsv(csv, `${safeName}-users-activity-${todayStamp()}.csv`);
}
